import {
  MapTrifold,
  Ticket,
  Clock,
  Globe,
  Phone,
  Sparkle,
  Warning,
} from '@phosphor-icons/react';
import React, { useState, useRef, memo } from 'react';
import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../../../../shared/Buttons';
import { VendorBusinessLocationWithDeals } from '../../../../../../types/apiTypes';
import { FixedSizeList as List } from 'react-window';
import useViewportHeight from '../../../../../../hooks/useViewportHeight';
import { sanitizeUrl } from '../../../../../../utils/SanitizationUtils';

const DraggableSearchResults = ({
  filteredVendorLocations,
  setSelectedVendorLocation,
  selectedVendorLocation,
  distances,
  hoursOfOperationStringify,
  loadingVendorLocations,
  hasMore,
  loadingMore,
  onLoadMore,
}: {
  filteredVendorLocations: VendorBusinessLocationWithDeals[];
  setSelectedVendorLocation: (
    loc: VendorBusinessLocationWithDeals | null
  ) => void;
  selectedVendorLocation: VendorBusinessLocationWithDeals | null;
  distances: any;
  hoursOfOperationStringify: any;
  loadingVendorLocations: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}) => {
  const [dragging, setDragging] = useState(false);
  const [startY, setStartY] = useState(0); // Starting touch position
  const [currentY, setCurrentY] = useState(window.innerHeight - 280); // Default position (middle)
  const [lastY, setLastY] = useState(window.innerHeight - 280); // Last snapped position
  const dragHandleRef = useRef<HTMLDivElement>(null); // Reference to the drag handle

  const TOP_POSITION = 126; // Top of the viewport
  const MIDDLE_POSITION = window.innerHeight - 280; // Middle position
  const BOTTOM_POSITION = window.innerHeight - 80; // Visible above the bottom, drag handle remains visible

  useViewportHeight();

  const handleTouchStart = (e: React.TouchEvent) => {
    // Check if the touch originates from the drag handle
    if (
      dragHandleRef.current &&
      dragHandleRef.current.contains(e.target as Node)
    ) {
      setDragging(true);
      setStartY(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Allow native scrolling for the scrollable container
    const target = e.target as HTMLElement;
    if (target.closest('.scrollable')) {
      return;
    }

    if (!dragging) return;

    const deltaY = e.touches[0].clientY - startY;
    const newY = Math.min(
      Math.max(lastY + deltaY, TOP_POSITION),
      BOTTOM_POSITION
    ); // Restrict movement within bounds
    setCurrentY(newY);
  };

  const handleTouchEnd = () => {
    if (!dragging) return;

    setDragging(false);

    // Calculate the distance to each position
    const distanceToTop = Math.abs(currentY - TOP_POSITION);
    const distanceToMiddle = Math.abs(currentY - MIDDLE_POSITION);
    const distanceToBottom = Math.abs(currentY - BOTTOM_POSITION);

    // Snap to the nearest position
    const snapPosition =
      distanceToTop < distanceToMiddle && distanceToTop < distanceToBottom
        ? TOP_POSITION
        : distanceToMiddle < distanceToBottom
          ? MIDDLE_POSITION
          : BOTTOM_POSITION;

    setCurrentY(snapPosition);
    setLastY(snapPosition);
  };

  // Memoized row renderer for the virtualized list
  const VendorLocationRow = memo(
    ({
      index,
      style,
      data,
    }: {
      index: number;
      style: React.CSSProperties;
      data: any;
    }) => {
      const {
        filteredVendorLocations,
        selectedVendorLocation,
        setSelectedVendorLocation,
        distances,
        hoursOfOperationStringify,
      } = data;
      const vendorLocation = filteredVendorLocations[index];

      return (
        <div style={style} className="px-2 py-1">
          <div
            key={vendorLocation?.location_id}
            onClick={() => {
              if (
                vendorLocation?.location_id ===
                selectedVendorLocation?.location_id
              ) {
                setSelectedVendorLocation(null);
              } else {
                setSelectedVendorLocation(vendorLocation);
              }
            }}
            className={`flex h-[240px] flex-col justify-between gap-y-1 rounded-lg border bg-white p-4 hover:cursor-pointer hover:bg-gray-50 ${
              selectedVendorLocation?.location_id ===
                vendorLocation?.location_id && 'border-accent'
            }`}
          >
            <div className="flex justify-between">
              <div className="flex flex-col">
                <h2 className="max-w-[200px] text-[16px] font-[600] text-primary">
                  {vendorLocation?.vendor?.business_name}
                </h2>
                {vendorLocation?.location_name !==
                  vendorLocation?.vendor?.business_name && (
                  <h2 className="max-w-[200px] text-wrap text-[14px] font-[600] text-graytertiary">
                    {vendorLocation?.location_name}
                  </h2>
                )}
              </div>
              <div className="flex flex-row items-center space-x-2 text-[12px] text-gray-500">
                <div className="flex flex-row items-center space-x-[2px] rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[4px] py-[3px] align-middle">
                  <span>{distances[vendorLocation?.location_id]}</span>
                </div>
              </div>
            </div>
            {vendorLocation?.vendor?.is_highly_recommended && (
              <div className="flex w-full justify-end">
                <div className="flex max-w-[120px] items-center justify-center space-x-[2px] rounded-full border-[1px] border-accent bg-yellow-50 px-[4px] py-[1px] text-[12px] text-accent">
                  <Sparkle size={16} />
                  <p>Recommended</p>
                </div>
              </div>
            )}
            {vendorLocation?.vendor?.is_not_recommended && (
              <div className="flex w-full justify-end">
                <div className="flex max-w-[150px] items-center justify-center space-x-[2px] rounded-full border-[1px] border-danger bg-red-50 px-[4px] py-[1px] text-[12px] text-danger">
                  <Warning size={16} />
                  <p>Not Recommended</p>
                </div>
              </div>
            )}
            <div className="h-[20px] max-h-[20px] w-[321px] max-w-[321px] overflow-ellipsis">
              <p className="line-clamp-1 text-sm text-gray-500">
                {vendorLocation?.description_of_services}
              </p>
            </div>
            <div className="flex w-full flex-row items-center justify-between align-middle">
              {vendorLocation?.hours_of_operation && (
                <div className="flex w-[60%] max-w-[60%] flex-row items-center space-x-2 text-sm text-gray-600">
                  <Clock
                    size={16}
                    className="w-[16px] min-w-[16px] text-primary opacity-50"
                  />
                  <span className="line-clamp-2 overflow-ellipsis">
                    {hoursOfOperationStringify(
                      vendorLocation?.hours_of_operation ?? ''
                    )}
                  </span>
                </div>
              )}
              {vendorLocation?.deals && vendorLocation?.deals?.length > 0 && (
                <div className="flex items-center space-x-2 rounded-[24px] border-[1px] border-bordergray bg-[#E7F8F0] px-2 py-1">
                  <span className="flex flex-row items-center space-x-1 align-middle text-sm text-[#037355]">
                    <Ticket size={20} /> <p>Promotion</p>
                  </span>
                </div>
              )}
            </div>
            {vendorLocation?.website_url && (
              <div className="flex flex-row items-center space-x-2 align-middle">
                <Globe size={16} className="text-primary opacity-50" />
                <a
                  href={sanitizeUrl(vendorLocation?.website_url)}
                  className="line-clamp-1 w-[74%] overflow-ellipsis text-[14px] text-primary underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vendorLocation?.website_url}
                </a>
              </div>
            )}
            <div className="relative flex w-full flex-row items-center justify-between align-middle">
              <div className="flex w-[65%] flex-row items-center space-x-2 align-middle">
                <Phone size={16} className="text-primary opacity-50" />
                <a
                  href={`tel:${vendorLocation?.business_phone_number}`}
                  className="text-[14px] text-primary underline"
                >
                  {vendorLocation?.business_phone_number}
                </a>
              </div>
              {vendorLocation?.vendor?.is_highly_recommended && (
                <img
                  src="/TACH Certified Vendor.png"
                  alt="TACH Certified Vendor"
                  className="absolute bottom-0 right-0 h-14 w-auto object-contain"
                />
              )}
            </div>
          </div>
        </div>
      );
    }
  );

  const calculateListHeight = () => {
    if (currentY === MIDDLE_POSITION) {
      return 184; // Height for middle position
    } else if (currentY === TOP_POSITION) {
      return window.innerHeight - TOP_POSITION - 64; // Height for top position
    } else {
      return 0; // No height when at bottom position
    }
  };

  return (
    <>
      {currentY === TOP_POSITION && (
        <AccentButton
          onClick={() => {
            setCurrentY(MIDDLE_POSITION);
            setLastY(MIDDLE_POSITION);
            setStartY(MIDDLE_POSITION);
          }}
          addClass="absolute z-40 bottom-2 left-[30%] rounded-full h-[48px] w-[40%] flex flex-row items-center align-middle justify-center space-x-2"
        >
          <MapTrifold size={20} />
          <p className="text-[16px] text-primary">Show Map</p>
        </AccentButton>
      )}
      <div
        className="absolute left-0 z-10 w-full bg-primarybg p-[6px]"
        style={{
          top: `${currentY}px`,
          bottom: 0,
          transition: dragging ? 'none' : 'top 0.3s ease-in-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        {currentY !== TOP_POSITION && (
          <div
            ref={dragHandleRef}
            className="relative flex h-[30px] cursor-pointer items-center justify-center"
          >
            <div className="h-[5px] w-[80px] rounded-full bg-gray-400" />
          </div>
        )}

        {/* Scrollable Content */}
        {currentY !== BOTTOM_POSITION && (
          <div
            className="scrollable touch-auto overflow-y-auto overflow-x-hidden"
            style={{
              height: `calc(var(--vvh, 100dvh) - ${currentY}px - 64px)`,
            }}
          >
            {filteredVendorLocations && filteredVendorLocations?.length > 0 ? (
              <List
                height={calculateListHeight()}
                width="100%"
                itemCount={filteredVendorLocations.length}
                itemSize={248} // Height of each vendor location item with margin
                itemData={{
                  filteredVendorLocations,
                  selectedVendorLocation,
                  setSelectedVendorLocation,
                  distances,
                  hoursOfOperationStringify,
                }}
                className="relative w-full pb-20"
                onItemsRendered={({ visibleStopIndex }) => {
                  if (
                    hasMore &&
                    !loadingMore &&
                    visibleStopIndex >= filteredVendorLocations.length - 5
                  ) {
                    onLoadMore();
                  }
                }}
              >
                {VendorLocationRow}
              </List>
            ) : !loadingVendorLocations ? (
              <div className="align-middleh-full flex h-full w-full items-center justify-center">
                <h2>No vendors found with these criteria.</h2>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingVendorLocations}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DraggableSearchResults;
